import {
  ALERT_MESSAGE_TYPE,
  authAtom,
  getPreReservation,
  getSureUserId,
  PAGE_NAME,
  removeSureUserId,
  SESSION_STORAGE_KEY,
  useConfirmPreReservationPayment,
  useDetectMobile,
  useRecoil,
} from '@orcar/common-d2c';
import { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getReservationAttribution } from '@/utils/analytics';
import { kakaoTrackPurchase } from '@/utils/kakao-pixel';
import { mobonTrackPurchase } from '@/utils/mobon';
import { addConfirmReservationTrackingScript } from '@/utils/script';
import * as S from '../styles';

const RequestPaymentPage = () => {
  const navigate = useNavigate();

  const { recoilValue: user } = useRecoil(authAtom);

  const [searchParams] = useSearchParams();
  const paymentKey = searchParams.get('paymentKey');
  const amount = Number(searchParams.get('amount'));
  const orderId = searchParams.get('orderId');

  const hasRequestedPayment = useRef(false);

  const { isMobile, screenHeight } = useDetectMobile();

  const confirmPayment = useConfirmPreReservationPayment();

  useEffect(() => {
    const sureUserId = getSureUserId();
    const referralCode =
      sessionStorage.getItem(SESSION_STORAGE_KEY.REFERRAL_CODE) || undefined;
    const preReservationId = sessionStorage.getItem(
      SESSION_STORAGE_KEY.PRE_RESERVATION_ID,
    );
    const isFullCoverage =
      sessionStorage.getItem(SESSION_STORAGE_KEY.IS_FULL_COVERAGE) === 'true';
    const mileageAmount =
      Number(sessionStorage.getItem(SESSION_STORAGE_KEY.USED_MILEAGE)) ||
      undefined;
    const applyReferralDiscount =
      sessionStorage.getItem(SESSION_STORAGE_KEY.APPLY_REFERRAL) === 'true';
    // 인플루언서 주유권 코드 — 가격은 그대로, 예약 메모에 태그로만 남겨 실무자가
    // 반납 후 주유권을 보낸다.
    const influencerCode =
      sessionStorage.getItem('jejuro_influencer_code') || '';

    if (!paymentKey || !amount || !orderId || !preReservationId) {
      alert(ALERT_MESSAGE_TYPE.NOT_ALLOWED_ACCESS);
      navigate(PAGE_NAME.index);
      return;
    } else {
      getPreReservation(preReservationId)
        .then((res) => {
          if (!hasRequestedPayment.current && !res?.reservation) {
            hasRequestedPayment.current = true;

            confirmPayment
              .mutateAsync({
                id: orderId,
                input: {
                  paymentKey,
                  amount,
                  referralCode,
                  isFullCoverage,
                  sureUserId: sureUserId ? Number(sureUserId) : undefined,
                  mileageAmount,
                  applyReferralDiscount,
                  // 주유권 대상 표시 — 예약 거래처 메모 앞에 붙어 ERP에 노출된다.
                  ...(influencerCode
                    ? { memo: `🎁주유권: ${influencerCode}` }
                    : {}),
                  // 유입 채널을 ERP DB에 남겨 광고 성과를 실매출과 조인한다.
                  ...getReservationAttribution(),
                },
              })
              .then((confirmed) => {
                addConfirmReservationTrackingScript(user?.email || '', amount);
                // 카카오 픽셀 구매 전환(금액 포함)
                kakaoTrackPurchase({ totalPrice: amount });
                // 모비온(원태그) 간편결제 전환(주문·상품·금액 포함).
                // orderId는 결제 orderId(=pre-reservation id)가 아니라 실제
                // 예약번호(reservationId)로 보낸다 — 모비온 전환과 예약 대조를 위해.
                mobonTrackPurchase({
                  orderId: confirmed?.reservationId ?? String(orderId),
                  totalPrice: amount,
                  productId: res?.vehicleModelId
                    ? String(res.vehicleModelId)
                    : undefined,
                  productName: res?.vehicleModel?.name,
                  memberId: user?.id ? String(user.id) : undefined,
                });

                navigate(PAGE_NAME.confirm_payment, {
                  replace: true,
                });
              })
              .catch(() => {
                navigate(`${PAGE_NAME.payment_fail}`, {
                  replace: true,
                });
              })
              .finally(() => {
                sessionStorage.removeItem(SESSION_STORAGE_KEY.IS_FULL_COVERAGE);
                sessionStorage.removeItem(SESSION_STORAGE_KEY.REFERRAL_CODE);
                sessionStorage.removeItem(SESSION_STORAGE_KEY.USED_MILEAGE);
                sessionStorage.removeItem(SESSION_STORAGE_KEY.APPLY_REFERRAL);
                sessionStorage.removeItem('jejuro_influencer_code');
                removeSureUserId();

                window.gtag?.('event', 'conversion', {
                  send_to: 'AW-11240059564/I0F5COSskbQYEKzl1u8p',
                  value: res.price,
                  currency: 'KRW',
                  transaction_id: '',
                });

                window.wcs?.trans({
                  type: 'purchase',
                  items: [
                    {
                      productInfo: {
                        vehicleModelName: res?.vehicleModel?.name,
                        insuranceName: res?.insuranceName,
                      },
                      paymentInfo: res?.d2cPayment,
                    },
                  ],
                });
              });
          }
        })
        .catch(() => {
          alert(ALERT_MESSAGE_TYPE.FAIL_GET_PRODUCT);
          navigate(PAGE_NAME.index);
        });
    }
  }, [paymentKey, amount, orderId]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <S.Wrapper isMobile={isMobile} screenHeight={screenHeight - 148}>
      <S.Text type='main' isMobile={isMobile}>
        결제가 진행중입니다.
      </S.Text>
    </S.Wrapper>
  );
};

export default RequestPaymentPage;
